import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import { MdNotificationsActive, MdRefresh } from "react-icons/md";
import axios from "axios";
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const SendFeeNotifications = () => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSend = !readOnlyRoles.includes(userObj.role_name?.trim());

  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchPreview = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(baseUrl + "/main/getstudentfeenotifications", {
        params: {
          academic_year_id: userObj.academic_year_id,
          school_id: userObj.school_id,
        },
      });
      setStudents(Array.isArray(res.data) ? res.data : []);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error fetching fee notification preview:", error);
      toast.error("Failed to load fee due list");
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      [s.student_name, s.admission_number, s.class_name, s.section_name]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [students, searchQuery]);

  // Rows the send will actually target: the ticked ones, else everything listed
  const targetRows = useMemo(() => {
    if (!selectedIds.length) return filteredRecords;
    const picked = new Set(selectedIds.map(String));
    return students.filter((s) => picked.has(String(s.student_id)));
  }, [students, filteredRecords, selectedIds]);

  // Client-side estimate shown before sending (server figures are authoritative)
  const preview = useMemo(() => {
    let recipients = 0; // parents that have a login (get an in-app record)
    let pushReachable = 0; // parents that also have an app token (get a push)
    for (const s of targetRows) {
      if (s.father_user_id) recipients += 1;
      if (s.mother_user_id) recipients += 1;
      if (s.father_fcmtoken) pushReachable += 1;
      if (s.mother_fcmtoken) pushReachable += 1;
    }
    return {
      students: targetRows.length,
      recipients,
      pushReachable,
      recordOnly: Math.max(recipients - pushReachable, 0),
    };
  }, [targetRows]);

  const targetCount = preview.students;

  const handleSend = async () => {
    setIsSending(true);
    try {
      const body = {
        academic_year_id: userObj.academic_year_id,
        school_id: userObj.school_id,
        created_by: userObj.username || userObj.user_name || userObj.email,
      };
      if (selectedIds.length) body.student_ids = selectedIds.map(Number);

      const res = await axios.post(baseUrl + "/main/sendstudentfeenotifications", body);
      const s = res.data?.summary || {};
      toast.success(
        `Fee notifications: ${s.sent || 0} pushed, ${s.recorded_only || 0} recorded (no app), ` +
          `${s.failed || 0} failed, ${s.skipped || 0} skipped`
      );
      setShowConfirm(false);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404) {
        toast.error(
          "No active 'Fee' (FN) notification is configured. Create one under Create Notifications first."
        );
      } else {
        console.error("Error sending fee notifications:", error);
        toast.error("Failed to send fee notifications");
      }
    } finally {
      setIsSending(false);
    }
  };

  const columns = [
    {
      field: "student_name",
      headerName: "Student",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Tooltip title={p.value || ""}>
          <span className="cell-wrap">{p.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "admission_number",
      headerName: "Adm. No.",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "class_section",
      headerName: "Class",
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      valueGetter: (p) => `${p.row.class_name || ""}-${p.row.section_name || ""}`,
    },
    {
      field: "current_schedule_name",
      headerName: "Schedule",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Tooltip title={p.value || ""}>
          <span className="cell-wrap">{String(p.value || "").trim()}</span>
        </Tooltip>
      ),
    },
    {
      field: "current_due_date",
      headerName: "Due Date",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "amount_to_be_paid",
      headerName: "Amount Due",
      flex: 0.8,
      headerAlign: "center",
      align: "right",
      renderCell: (p) => <span>Rs.{p.value}</span>,
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 0.8,
      headerAlign: "center",
      align: "right",
      renderCell: (p) => <span>Rs.{p.value}</span>,
    },
    {
      field: "reachable",
      headerName: "Parents Reachable",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (p) => {
        const father = !!p.row.father_fcmtoken;
        const mother = !!p.row.mother_fcmtoken;
        return (
          <span className="cell-wrap">
            <span style={{ color: father ? "#198754" : "#adb5bd" }}>Father</span>
            {"  |  "}
            <span style={{ color: mother ? "#198754" : "#adb5bd" }}>Mother</span>
          </span>
        );
      },
    },
  ];

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h6 className="commonTableTitle">Send Fee Notifications</h6>
          <div className="d-flex align-items-center">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: "6px" }}>
            <Tooltip title="Refresh">
              <Button className="secondaryBtn" variant="secondary" onClick={fetchPreview}>
                <MdRefresh />
              </Button>
            </Tooltip>
            {canSend && (
              <Tooltip title="Preview recipients, then send">
                <Button
                  className="primaryBtn"
                  variant="primary"
                  disabled={isLoading || isSending || filteredRecords.length === 0}
                  onClick={() => setShowConfirm(true)}
                >
                  <MdNotificationsActive />
                  <span className="ms-1">
                    Preview &amp; Send{" "}
                    {selectedIds.length ? `(${selectedIds.length})` : "All"}
                  </span>
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(r) => r.student_id}
              showToolbar={false}
              pagination
              pageSizeOptions={[10, 20, 50]}
              density="compact"
              maxHeight={520}
              checkboxSelection
              selectionModel={selectedIds}
              onSelectionModelChange={(m) => setSelectedIds(Array.isArray(m) ? m : [m])}
            />
          )}
        </div>
      </div>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Fee Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            The active <strong>Fee (FN)</strong> notification will be sent to the
            father and mother of{" "}
            <strong>{targetCount}</strong>{" "}
            {selectedIds.length ? "selected" : "listed"} student
            {targetCount === 1 ? "" : "s"}.
          </p>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              overflow: "hidden",
              maxWidth: 380,
            }}
          >
            {[
              { label: "Students", value: preview.students, color: "#111827" },
              { label: "Parent recipients (in-app record)", value: preview.recipients, color: "#111827" },
              { label: "Will get a push (app installed)", value: preview.pushReachable, color: "#198754" },
              { label: "Recorded only (no app token)", value: preview.recordOnly, color: "#6b7280" },
            ].map((row, i) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  padding: "8px 12px",
                  borderTop: i === 0 ? "none" : "1px solid #f0f1f3",
                  background: i % 2 ? "#fafafa" : "#fff",
                }}
              >
                <span style={{ fontSize: 13, color: "#374151" }}>{row.label}</span>
                <strong style={{ fontSize: 14, color: row.color, whiteSpace: "nowrap" }}>
                  {row.value}
                </strong>
              </div>
            ))}
          </div>
          <small className="text-muted d-block mt-2">
            These are estimates from the list; the server response will confirm the
            exact sent / failed counts.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="secondaryBtn"
            onClick={() => setShowConfirm(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="primaryBtn"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? "Sending..." : "Send Now"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SendFeeNotifications;
